import type {
  GetSupportTicketsStatusGroup,
  SupportAttachmentDto,
  SupportAttachmentPolicyDto,
  SupportCommentDto,
  SupportPersonDto,
  SupportTicketSummaryDto,
  SupportTicketSummaryDtoResolutionCategory,
  SupportTicketSummaryDtoStatus,
} from '@/api/control/missionImpactAPI.schemas';

/** Labels needed to describe a person on a ticket. */
export interface SupportPersonLabels {
  you: string;
  teamMember: string;
  supportTeam: string;
}

/**
 * Returns how a submitter, comment author or uploader is shown: "You" for the signed-in user,
 * the display name when known, otherwise a generic customer or support label.
 */
export function getSupportPersonLabel(person: SupportPersonDto | null | undefined, labels: SupportPersonLabels): string {
  if (!person) return labels.teamMember;
  if (person.isCurrentUser) return labels.you;
  if (person.displayName) return person.displayName;
  return person.type === 'Customer' ? labels.teamMember : labels.supportTeam;
}

/** Why a file can't be attached, if it can't. */
export type AttachmentRejection = 'tooLarge' | 'typeNotAllowed' | 'nameTooLong';

/**
 * Returns a file name's extension, lower-cased with a leading dot, or '' when it has none.
 */
export function getFileExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(dot).toLowerCase() : '';
}

/**
 * Validates a file against the Control Plane's attachment policy before uploading it. Like
 * Multiservice, types are checked by file extension only. The server repeats these checks;
 * this only gives early feedback.
 */
export function validateSupportAttachment(
  file: Pick<File, 'name' | 'size'>,
  policy: SupportAttachmentPolicyDto | undefined
): AttachmentRejection | null {
  if (!policy) return null;
  if (policy.maxBytes !== undefined && file.size > policy.maxBytes) return 'tooLarge';
  if (policy.fileNameMaxLength !== undefined && file.name.length > policy.fileNameMaxLength) return 'nameTooLong';
  const extension = getFileExtension(file.name);
  const blocked = (policy.blockedExtensions ?? []).map((e) => e.toLowerCase());
  if (blocked.includes(extension)) return 'typeNotAllowed';
  const allowed = (policy.allowedExtensions ?? []).map((e) => e.toLowerCase());
  if (allowed.length > 0 && !allowed.includes(extension)) return 'typeNotAllowed';
  return null;
}

/**
 * Formats a byte count for display (e.g. 1.5 MB).
 */
export function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined || bytes < 0) return '-';
  if (bytes < 1024) return `${String(bytes)} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${String(rounded)} ${units[unit]}`;
}

/**
 * What a ticket's status pill shows: its status, or "waiting on you" when support is waiting on the
 * customer's reply. Only that state asks the customer to act, so only it uses the strong amber.
 */
export type SupportTicketState = SupportTicketSummaryDtoStatus | 'WaitingOnYou';

/**
 * Status pill classes. Colour answers "whose move is it": neutral while support has it or it's
 * closed, blue (info) while support works on it, green once resolved, strong amber when it's the
 * customer's turn.
 */
export const SUPPORT_STATE_CLASSES: Record<SupportTicketState, string> = {
  WaitingOnYou: 'background-warning-strong text-warning-strong border-warning-strong',
  Open: 'background-info text-info border-info',
  InProgress: 'background-info-strong text-info-strong border-info-strong',
  Resolved: 'background-success text-success border-success',
  Closed: 'background-neutral text-neutral border-neutral',
};

/**
 * Returns the state a ticket's pill shows, or undefined when the status is unknown.
 */
export function getSupportTicketState(
  ticket: Pick<SupportTicketSummaryDto, 'status' | 'needsCustomerReply'>
): SupportTicketState | undefined {
  if (!ticket.status) return undefined;
  const isActive = ticket.status === 'Open' || ticket.status === 'InProgress';
  return isActive && ticket.needsCustomerReply ? 'WaitingOnYou' : ticket.status;
}

/** The ticket list tabs, in display order. Open is the default. */
export type SupportTicketTab = 'open' | 'needsReply' | 'closed';

/** Ticket list tabs in display order. */
export const SUPPORT_TICKET_TABS: SupportTicketTab[] = ['open', 'needsReply', 'closed'];

/** The status group each ticket list tab filters on. */
export const SUPPORT_TAB_STATUS_GROUPS: Record<SupportTicketTab, GetSupportTicketsStatusGroup> = {
  open: 'open',
  needsReply: 'needsReply',
  closed: 'closed',
};

/**
 * Display names for resolution categories. These are Multiservice values and are shown as-is in
 * every language.
 */
export const RESOLUTION_CATEGORY_LABELS: Record<NonNullable<SupportTicketSummaryDtoResolutionCategory>, string> = {
  Bug: 'Bug/Issue',
  Knowledge: 'Knowledge',
  Enhancement: 'Enhancement',
  DataWrangler: 'Data Wrangler',
};

/**
 * Whether a ticket involves a Data Wrangler (by the category it was raised with, or the one support
 * resolved it as), so the organization's consultant hours are shown with it.
 */
export function usesConsultantHours(
  ticket: Pick<SupportTicketSummaryDto, 'category' | 'resolutionCategory'>
): boolean {
  return ticket.category === 'DataWrangler' || ticket.resolutionCategory === 'DataWrangler';
}

/** The pinned AI summary and the rest of a ticket's conversation. */
export interface SupportConversationParts {
  aiSummary: SupportCommentDto | undefined;
  replies: SupportCommentDto[];
}

/**
 * Splits a ticket's comments into the AI summary (the most recent AI comment, pinned first) and the
 * remaining replies, newest first so the latest context is always at the top.
 */
export function splitConversation(comments: SupportCommentDto[] | null | undefined): SupportConversationParts {
  const time = (comment: SupportCommentDto) => (comment.createdAt ? Date.parse(comment.createdAt) : 0);
  const newestFirst = [...(comments ?? [])].sort((a, b) => time(b) - time(a));
  const aiSummary = newestFirst.find((comment) => comment.author?.type === 'AI');
  return { aiSummary, replies: newestFirst.filter((comment) => comment !== aiSummary) };
}

/**
 * Returns the attachments posted with a comment, excluding images embedded in its HTML.
 */
export function getCommentAttachments(
  comment: SupportCommentDto,
  attachments: SupportAttachmentDto[] | null | undefined
): SupportAttachmentDto[] {
  const ids = new Set(comment.attachmentIds ?? []);
  return (attachments ?? []).filter(
    (attachment) =>
      !attachment.isInline && ((attachment.id !== undefined && ids.has(attachment.id)) || attachment.commentId === comment.id)
  );
}

/**
 * Returns the attachments listed in the ticket's attachments panel, newest first: everything except
 * images embedded in HTML comments. Attachments without an upload time go last.
 */
export function getListedAttachments(attachments: SupportAttachmentDto[] | null | undefined): SupportAttachmentDto[] {
  return (attachments ?? [])
    .filter((attachment) => !attachment.isInline)
    .sort((a, b) => getUploadTime(b) - getUploadTime(a));
}

/**
 * Returns when an attachment was uploaded, in milliseconds, or 0 when unknown or invalid.
 */
function getUploadTime(attachment: SupportAttachmentDto): number {
  const time = attachment.uploadedAt ? Date.parse(attachment.uploadedAt) : NaN;
  return Number.isNaN(time) ? 0 : time;
}

/**
 * Whether an attachment is an image that can be previewed as a thumbnail.
 */
export function isImageAttachment(attachment: Pick<SupportAttachmentDto, 'fileType'>): boolean {
  return (attachment.fileType ?? '').toLowerCase().startsWith('image/');
}

/** Comments longer than this many characters, or lines, start collapsed. */
const LONG_COMMENT_CHARACTERS = 600;

/** Comments with more lines than this start collapsed. */
const LONG_COMMENT_LINES = 6;

/**
 * Whether a comment is long enough to start collapsed behind "Show full reply".
 */
export function isLongComment(text: string | null | undefined): boolean {
  if (!text) return false;
  return text.length > LONG_COMMENT_CHARACTERS || text.split('\n').length > LONG_COMMENT_LINES;
}

/**
 * Returns up to two initials for an avatar, or '?' when the name is empty.
 */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return (first + last).toUpperCase();
}

/**
 * Formats a number of consultant hours (up to two decimals) for display.
 */
export function formatHours(hours: number | undefined, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(hours ?? 0);
}

/** Stripe currencies whose amounts are already in whole units rather than cents. */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);

/**
 * Formats a Stripe package price given in minor units (e.g. cents), or '' when the price is unknown.
 */
export function formatPackagePrice(unitAmount: number | undefined, currency: string | undefined, locale: string): string {
  if (unitAmount === undefined || !currency) return '';
  const code = currency.toUpperCase();
  const amount = ZERO_DECIMAL_CURRENCIES.has(code) ? unitAmount : unitAmount / 100;
  return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(amount);
}

/**
 * Maps a UI language to the locale used for date formatting.
 */
export function getDateLocale(language: string): string {
  switch (language) {
    case 'fr':
      return 'fr-FR';
    case 'de':
      return 'de-DE';
    case 'es':
      return 'es-ES';
    default:
      return 'en-US';
  }
}

/**
 * Formats an ISO timestamp as a short date and time, or a dash when missing.
 */
export function formatSupportDate(value: string | null | undefined, locale: string): string {
  if (!value) return '-';
  return new Date(value).toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Replaces {placeholders} in a translated string.
 */
export function fillSupportTemplate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => (key in values ? String(values[key]) : match));
}

/** How often a ticket is refetched while an attachment is still being malware scanned. */
export const SCAN_POLL_INTERVAL_MS = 5_000;

/** Stops polling after this many refetches (about 10 minutes), in case a scan never finishes. */
export const SCAN_POLL_MAX_UPDATES = 120;

/**
 * Returns the refetch interval for a ticket: {@link SCAN_POLL_INTERVAL_MS} while any attachment is
 * still being scanned, otherwise false so polling stops once every scan has finished.
 */
export function getScanPollInterval(
  attachments: SupportAttachmentDto[] | null | undefined,
  updateCount: number
): number | false {
  const hasPendingScan = (attachments ?? []).some((attachment) => attachment.scanStatus === 'Pending');
  return hasPendingScan && updateCount < SCAN_POLL_MAX_UPDATES ? SCAN_POLL_INTERVAL_MS : false;
}

/**
 * Builds a partner logo request's cache-busting version from the logo update times, so a replaced logo
 * gets a new URL (and query key). Returns undefined when there are no update times.
 */
export function getLogoVersionParams(...updatedAt: (string | null | undefined)[]): { v: string } | undefined {
  if (!updatedAt.some(Boolean)) return undefined;
  return { v: updatedAt.map((u) => u ?? '').join('|') };
}
